<template>
    <bottom-sheet>
        <template #title>
            <span>Buy size rule {{ currentAsset.symbol }}</span>
        </template>

        <form autocomplete="off" @submit.prevent="onSubmit">
            <sheet-section label="When">
                <form-row v-for="(condition, index) in conditions" :key="index">
                    <template #label>
                        <span class="rule-metric">
                            <sheet-select v-model="condition.metric" :options="metricOptions" />
                        </span>
                    </template>
                    <span class="rule-below">below</span>
                    <input
                        v-model.number="condition.below"
                        type="number"
                        step="any"
                        inputmode="decimal"
                        class="sheet-input rule-input"
                        :placeholder="condition.metric === 'free_shares' ? '#' : '$'"
                    >
                    <button
                        type="button"
                        class="rule-remove"
                        :disabled="conditions.length === 1"
                        @click.prevent="removeCondition(index)"
                    >&times;</button>
                </form-row>
                <div class="sheet-row">
                    <button type="button" class="sheet-text-btn" @click.prevent="addCondition">+ Add condition</button>
                </div>
            </sheet-section>

            <sheet-section label="Buy">
                <form-row label="Amount">
                    <sheet-select v-model="positions" :options="buySizeOptions" />
                </form-row>
            </sheet-section>
        </form>

        <template #actions>
            <button type="button" class="sheet-btn sheet-btn-primary" :disabled="!canSave" @click="onSubmit">Save rule</button>
        </template>
    </bottom-sheet>
</template>

<script>
import { mapState } from 'vuex';
import formatter from '@/mixins/formatter';

import { MARKET_DEFAULT_BUY_POSITIONS, MAX_BUY_SIZE_RULE_POSITIONS } from '@/constants/trading';
import BottomSheet from '@/components/sheet/BottomSheet.vue';
import SheetSection from '@/components/sheet/SheetSection.vue';
import FormRow from '@/components/sheet/FormRow.vue';
import SheetSelect from '@/components/sheet/SheetSelect.vue';

export default {
    name: 'EditBuySizeRule',
    props: ['currentAsset', 'rule', 'ruleIndex', 'onSave'],
    mixins: [formatter],
    components: { BottomSheet, SheetSection, FormRow, SheetSelect },
    data() {
        const marketDefault = MARKET_DEFAULT_BUY_POSITIONS[this.currentAsset.market] ?? 1;

        return {
            conditions: this.rule
                ? this.rule.conditions.map(condition => ({ ...condition }))
                : [{ metric: 'holdings_value', below: null }],
            positions: this.rule
                ? this.rule.positions
                : (this.currentAsset.meta?.defaultNumberPositions || marketDefault),
            metricOptions: [
                { value: 'holdings_value', text: 'Holdings value' },
                { value: 'price', text: 'Price' },
                { value: 'free_shares', text: 'Free shares (count)' }
            ],
        }
    },
    methods: {
        addCondition() {
            this.conditions.push({ metric: 'price', below: null });
        },
        removeCondition(index) {
            this.conditions.splice(index, 1);
        },
        onSubmit() {
            if (!this.canSave) return;

            this.onSave(this.builtRule, this.ruleIndex);
            this.$FModal.hide();
        },
    },
    computed: {
        ...mapState({
            defaultPositionSize: state => state.positions.defaultPositionSize,
        }),
        buySizeOptions() {
            const options = [];
            for (let i = 1; i <= MAX_BUY_SIZE_RULE_POSITIONS; i++) {
                options.push({ value: i, text: this.formatAmount(i * this.defaultPositionSize) });
            }
            return options;
        },
        builtRule() {
            return {
                conditions: this.conditions.map(condition => ({ metric: condition.metric, below: condition.below })),
                positions: this.positions,
            };
        },
        canSave() {
            if (!(this.positions > 0)) return false;

            return this.conditions.every(condition => condition.metric && condition.below > 0);
        },
    },
}
</script>

<style scoped>
/* positioned, so the metric picker covers only the metric and not the value field */
.rule-metric {
    position: relative;
    color: #fff;
}
.rule-below {
    color: var(--sheet-label);
    font-size: 13px;
}
.sheet-input.rule-input {
    width: 84px;
}
.rule-remove {
    padding: 0 0 0 4px;
    border: 0;
    background: none;
    color: var(--sheet-muted);
    font-size: 18px;
    line-height: 1;
}
.rule-remove:disabled {
    opacity: .3;
}
</style>
